import React, { useEffect, useState } from 'react';
import {
    Container,
    Grid,
    Typography,
    Box,
    Paper
} from '@mui/material';
import {
    Games,
    Schedule,
    People,
    TrendingUp,
    Timer,
    Star
} from '@mui/icons-material';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import configureAmplify from '../configuration/amplify';
import DashboardCard from '../components/DashboardCard';
import RecentActivity from '../components/RecentActivity';
import QuickActions from '../components/QuickActions';
import RealtimeMetrics from '../components/analytics/RealtimeMetrics';
import UserEngagementChart from '../components/analytics/UserEngagementChart';
import GamePerformanceChart from '../components/analytics/GamePerformanceChart';
import AnalyticsService from '../services/AnalyticsService';

Amplify.configure(configureAmplify());

const Home: React.FC = () => {
    const [todayData, setTodayData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Fetch today's analytics data for dashboard cards
        fetchTodayData();
    }, []);

    const fetchTodayData = async () => {
        try {
            setLoading(true);
            const data = await AnalyticsService.fetchAnalytics({
                metrics: [
                    'activeUsers',
                    'sessions',
                    'screenPageViews',
                    'engagementRate'
                ],
                startDate: 'today',
                endDate: 'today'
            });
            setTodayData(data);
        } catch (error) {
            console.error('Error fetching today analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (value: string | undefined): string => {
        if (!value) return '0';
        const num = parseInt(value);
        return num.toLocaleString();
    };

    const formatPercentage = (value: string | undefined): string => {
        if (!value) return '0%';
        const num = parseFloat(value) * 100;
        return `${num.toFixed(1)}%`;
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Dashboard
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Welcome to Famous Initials Admin Panel - Real-time Analytics
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Today's Statistics Cards */}
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Today's Visitors"
                        value={todayData ? formatNumber(todayData.data.metrics.activeUsers) : '-'}
                        subtitle="Unique users today"
                        icon={<People />}
                        color="primary"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Today's Sessions"
                        value={todayData ? formatNumber(todayData.data.metrics.sessions) : '-'}
                        subtitle="Total visits today"
                        icon={<Games />}
                        color="success"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Today's Page Views"
                        value={todayData ? formatNumber(todayData.data.metrics.screenPageViews) : '-'}
                        subtitle="Pages viewed today"
                        icon={<Schedule />}
                        color="warning"
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <DashboardCard
                        title="Today's Engagement"
                        value={todayData ? formatPercentage(todayData.data.metrics.engagementRate) : '-'}
                        subtitle="Engaged sessions"
                        icon={<Star />}
                        color="secondary"
                        loading={loading}
                    />
                </Grid>

                {/* Real-time Analytics Overview */}
                <Grid item xs={12}>
                    <RealtimeMetrics />
                </Grid>

                {/* Main Content Row */}
                <Grid item xs={12} md={8}>
                    <RecentActivity />
                </Grid>
                <Grid item xs={12} md={4}>
                    <QuickActions />
                </Grid>

                {/* Analytics Charts */}
                <Grid item xs={12} lg={6}>
                    <UserEngagementChart />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <GamePerformanceChart />
                </Grid>
            </Grid>
        </Container>
    );
};

export default Home;